import { Injectable } from "@angular/core";
import { HttpService } from "app/js/src/infrastructure/http/http";
import { ITextTemplatesProvider } from "app/js/src/domain/clipping-studio/i-text-templates-provider";
import {
  ClippingStudioTextTemplate,
  ClippingStudioTextTemplateWritePayload,
} from "app/js/src/domain/clipping-studio/clipping-studio-text-template";
import {
  BOLD_FONT_WEIGHT,
  normalizeTextWeight,
} from "app/js/src/domain/clipping-studio/clipping-studio-text-layout";

/** Response item from GET /api/clipping-studio/text-templates (backend DTO, PascalCase). */
interface TextTemplateApiItem {
  Id: number;
  TenantId: number;
  Payload: Record<string, unknown> | string;
}

@Injectable()
export class ClippingStudioTextTemplatesHttpService extends ITextTemplatesProvider {
  private readonly url = "clipping-studio/text-templates";

  constructor(private readonly httpService: HttpService) {
    super();
  }

  async getTextTemplates(): Promise<ClippingStudioTextTemplate[]> {
    const raw = await this.httpService.get(this.url);
    const items = (Array.isArray(raw) ? raw : []) as TextTemplateApiItem[];
    return items.map((item) => this.mapItem(item));
  }

  async createTextTemplate(
    payload: ClippingStudioTextTemplateWritePayload,
  ): Promise<void> {
    await this.httpService.post(this.url, { Payload: payload });
  }

  async updateTextTemplate(
    id: number,
    payload: ClippingStudioTextTemplateWritePayload,
  ): Promise<void> {
    await this.httpService.put(`${this.url}/${id}`, { Payload: payload });
  }

  async deleteTextTemplate(id: number): Promise<void> {
    await this.httpService.delete(`${this.url}/${id}`);
  }

  private mapItem(item: TextTemplateApiItem): ClippingStudioTextTemplate {
    const p = this.parsePayload(item.Payload);
    const badgeRaw = p["badge"];
    const badge =
      typeof badgeRaw === "string" && badgeRaw.trim().length > 0
        ? badgeRaw
        : undefined;
    // Catalog styles feed timeline events, so collapse the weight to what the render can draw.
    const weight = normalizeTextWeight({
      fontWeight:
        typeof p["fontWeight"] === "string" ? p["fontWeight"] : BOLD_FONT_WEIGHT,
      bold: typeof p["bold"] === "boolean" ? p["bold"] : undefined,
    });

    return {
      id: Number(item.Id),
      badge,
      text: typeof p["text"] === "string" ? p["text"] : "",
      fontSize: typeof p["fontSize"] === "number" ? p["fontSize"] : 48,
      fontWeight: weight.fontWeight,
      fontStyle:
        typeof p["fontStyle"] === "string" ? p["fontStyle"] : undefined,
      fontColor: typeof p["fontColor"] === "string" ? p["fontColor"] : "#ffffff",
      fontFamily:
        typeof p["fontFamily"] === "string" ? p["fontFamily"] : undefined,
      alignment:
        typeof p["alignment"] === "string" ? p["alignment"] : undefined,
      bold: weight.bold,
      italic: typeof p["italic"] === "boolean" ? p["italic"] : undefined,
      backgroundColor:
        typeof p["backgroundColor"] === "string"
          ? p["backgroundColor"]
          : undefined,
      backgroundPadding:
        typeof p["backgroundPadding"] === "number"
          ? p["backgroundPadding"]
          : undefined,
      backgroundBorderRadius:
        typeof p["backgroundBorderRadius"] === "number"
          ? p["backgroundBorderRadius"]
          : undefined,
      dropShadow:
        typeof p["dropShadow"] === "boolean" ? p["dropShadow"] : undefined,
      shadowColor:
        typeof p["shadowColor"] === "string" ? p["shadowColor"] : undefined,
      shadowBlur:
        typeof p["shadowBlur"] === "number" ? p["shadowBlur"] : undefined,
      shadowOffsetX:
        typeof p["shadowOffsetX"] === "number" ? p["shadowOffsetX"] : undefined,
      shadowOffsetY:
        typeof p["shadowOffsetY"] === "number" ? p["shadowOffsetY"] : undefined,
    };
  }

  private parsePayload(
    payload: Record<string, unknown> | string | null | undefined,
  ): Record<string, unknown> {
    if (payload == null) return {};
    if (typeof payload === "object" && !Array.isArray(payload)) {
      return payload;
    }
    if (typeof payload === "string" && payload.trim().length > 0) {
      try {
        const parsed = JSON.parse(payload) as unknown;
        if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
          return parsed as Record<string, unknown>;
        }
      } catch {
        return {};
      }
    }
    return {};
  }
}
